import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ReauthDialogComponent } from './components/reauth-dialog/reauth-dialog.component';
import { AuthService } from './services/authService';
import { InactivityTimeoutService } from './services/inactivity-timeout.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'Seguro del Maestro';

  private reauthDialogRef: MatDialogRef<ReauthDialogComponent, 'reauthed' | 'logout'> | null = null;
  private subs = new Subscription();

  constructor(
    private router: Router,
    private inactivityTimeoutService: InactivityTimeoutService,
    private authService: AuthService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    // Inicializa el servicio de timeout (verifica sesión activa y sincroniza con otras pestañas)
    this.inactivityTimeoutService.initializeService();

    this.subs.add(
      this.inactivityTimeoutService.getReauthRequired().subscribe((required) => {
        if (required) {
          this.openReauthDialog();
        } else {
          this.closeReauthDialogIfOpen();
        }
      }),
    );

    // Logout desde otra pestaña: cerrar diálogo y ir a login
    const onStorage = (event: StorageEvent) => {
      if (event.key === 'token' && event.oldValue && !event.newValue) {
        this.closeReauthDialogIfOpen();
        this.router.navigateByUrl('/authentication/login', { replaceUrl: true });
      }
    };
    window.addEventListener('storage', onStorage);
    this.subs.add(new Subscription(() => window.removeEventListener('storage', onStorage)));

    // Reinicia el timer en cada navegación (salvo reauth pendiente)
    this.subs.add(
      this.router.events
        .pipe(filter((event) => event instanceof NavigationEnd))
        .subscribe(() => {
          if (this.inactivityTimeoutService.isReauthPending()) {
            return;
          }
          this.authService.isAuthenticated().subscribe((isAuth) => {
            if (isAuth) {
              this.inactivityTimeoutService.startInactivityTimer();
            }
          });
        }),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.inactivityTimeoutService.stopInactivityTimer();
  }

  private openReauthDialog(): void {
    if (this.reauthDialogRef) {
      return;
    }
    this.reauthDialogRef = this.dialog.open(ReauthDialogComponent, {
      disableClose: true,
      width: '420px',
      autoFocus: true,
      restoreFocus: true,
      backdropClass: 'reauth-dialog-backdrop',
    });
    this.reauthDialogRef.afterClosed().subscribe(() => {
      this.reauthDialogRef = null;
    });
  }

  private closeReauthDialogIfOpen(): void {
    if (this.reauthDialogRef) {
      this.reauthDialogRef.close('reauthed');
      this.reauthDialogRef = null;
    }
  }
}
